import type { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { TFunction } from 'i18next';
import {
  any,
  boolean,
  custom,
  date,
  object,
  string,
  type RefinementCtx,
  type ZodType
} from 'zod';

import type { PostFeatureSetting } from '@/features/feature-management/types/postTypeFeature';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import type { DorfHelferTypeEnum } from '@/features/posts/types/dorfHelferType';
import { PostFeedType } from '@/features/posts/types/postFeed';

type Props = {
  t: TFunction<'post-creation', 'screens.post-editor'>;
  postTypeFeature: PostFeatureSetting;
};

export const POST_TITLE_MAX_CHARS = 80;

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const basePostObject = ({ t, postTypeFeature }: Props) => {
  const maxChars = postTypeFeature.maxNumberCharsPerPost ?? 360;

  return object({
    channelType: custom<PostFeedType>(),
    marketType: custom<ClientPostTypeEnum>(),
    dorfHelferType: custom<DorfHelferTypeEnum>(),
    title: string()
      .trim()
      .max(
        POST_TITLE_MAX_CHARS,
        t('error.title.max', { maxChar: POST_TITLE_MAX_CHARS })
      )
      .optional(),
    text: string()
      .trim()
      .min(1, t('error.text.empty'))
      .max(maxChars, t('error.text.max', { maxChar: maxChars })),
    allDay: boolean(),
    startDate: date().optional(),
    endDate: date().optional(),
    location: any().optional(),
    suggestionCategory: any().optional(),
    poll: any().optional(),
    hiddenForOtherHomeAreas: boolean(),
    geoAreaIds: string().array().optional(),
    categories: string().optional(),
    organizationId: string().optional()
  });
};

const refineHappeningStartDate =
  (t: Props['t']) => (values: PostFormValues, ctx: RefinementCtx) => {
    if (
      values.channelType === PostFeedType.HAPPENING &&
      !isValidDate(values.startDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('error.date.required'),
        path: ['startDate']
      });
    }
  };

const refineSuggestionCategory =
  (t: Props['t']) => (values: PostFormValues, ctx: RefinementCtx) => {
    if (
      values.channelType === PostFeedType.SUGGESTION &&
      !values.suggestionCategory
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('error.category.required'),
        path: ['suggestionCategory']
      });
    }
  };

export const createPersonPostFormSchema = ({ t, postTypeFeature }: Props) =>
  basePostObject({ t, postTypeFeature })
    .superRefine(refineHappeningStartDate(t))
    .superRefine(refineSuggestionCategory(t)) satisfies ZodType<PostFormValues>;

export const createOrganizationPostFormSchema = ({
  t,
  postTypeFeature
}: Props) =>
  basePostObject({ t, postTypeFeature })
    .extend({
      geoAreaIds: string()
        .array()
        .optional()
        .refine((ids) => (ids?.length ?? 0) > 0, t('error.geoArea.required'))
    })
    .superRefine(refineHappeningStartDate(t)) satisfies ZodType<PostFormValues>;
